<script>
import { daysInWeek } from '@utils/filter-value-to-text'
import formatDate, { formatRequestData } from '@utils/format-date'
import lazyLoadComponent from '@router/lazyload-component'
import moment from 'moment'
import BaseButtonDialog from '@components/BaseButtonDialog'
import { messageStore } from '@utils/message'
import { notification } from '@utils/notification'

const minutesOfTime = (time = '00:00') => {
  const timeMatches = time.match(/^(\d+):(\d+)$/i)
  const [, hours, minutes] = timeMatches
  return Number(hours) * 60 + Number(minutes)
}

function initData() {
  return {
    startTime: '',
    endTime: '',
    repeat: '',
    repeatWith: [],
    repeatEndDate: '',
    productIds: [],
    serviceIds: [],
    serviceCategoryIds: [],
    state: 1,
    storeIds: [],
  }
}
export default {
  components: {
    BaseButtonDialog,
    CalendarItem: () => lazyLoadComponent(import("@views/schedule/components/CalendarItem.vue")),
    ServiceTree: () => lazyLoadComponent(import("@components/ServiceTree.vue")),
  },
  props: {
    defaultValue: {
      type: Object,
      default: () => initData(),
    },
    staffName: {
      type: String,
      required: true,
    },
    day: {
      type: String,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      startAndOverTime: [
        new Date(),
        new Date()
      ],
      sameLaunchTime: '',
      daysInWeek,
      // 下面是在限制選擇日期
      endDatePickerOptions: {
        disabledDate: (time) => {
          // check String or Date Object
          const limitWith365Days = new Date(this.day).getTime() + 3600 * 1000 * 24 * 90
          return time.getTime() < new Date(this.day).getTime() || time.getTime() >= limitWith365Days
        },
      },
      services: {},
      submitData: {},
      dialogVisible: false,
      modifyMode: 'self',
      removeMode: false,
      tryingToStoreItem: false,
      tryingToRemoveItem: false,
      priceList: [],
      checkAll: false,
      categoryList: [],
      selectedDate: null, // 選到的日子
      calendarEvents: [], // 在日曆上有哪些日子已經被點擊了
      timeRange: [
      ],
    }
  },
  computed: {
    isEditMode() {
      return this.defaultValue.id !== undefined
    },
    isModifyMode() {
      return this.isEditMode && !!this.defaultValue.repeat
    },
    /**
     * @description title -> 日期部分
     */
    title() {
      let day = this.defaultValue.startDate ?? this.day
      return `${formatDate(new Date(day), 'dateWeekDay')}`
    },
    availableService() {
      let list = []
      this.priceList.forEach((serviceItem) => {
        serviceItem.title = `${serviceItem.title} (${serviceItem.petCategoryTitle})`
        list.push(serviceItem)
      })
      return list
    },
    isIndeterminate() {
      this.checkAll = this.submitData.serviceIds.length === this.availableService.length
      if (this.submitData.serviceIds.length === 0) return false
      return this.submitData.serviceIds.length > 0 && this.submitData.serviceIds.length < this.availableService.length
    },
    isIndeterminateCategory() {
      this.checkAllCategory = this.submitData.serviceCategoryIds.length === this.categoryList.length
      if (this.submitData.serviceCategoryIds.length === 0) return false
      return this.submitData.serviceCategoryIds.length > 0 && this.submitData.serviceCategoryIds.length < this.categoryList.length
    },
    submitRules() {
      const repeatIsEmpty = this.submitData.repeat === ''
      const repeatIsWeekMode = this.submitData.repeat === 'week'
      return {
        title: [
          {
            required: true,
            message: '請填寫方案名稱',
            trigger: 'blur',
          },
        ],
        description: [
          {
            required: true,
            message: '請填寫方案內容',
            trigger: 'blur',
          },
        ],
        startTime: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value) return callback(new Error('請選擇時段開始時間'))
              if (this.submitData.endTime) this.$refs.form.validateField('endTime')

              callback()
            },
            trigger: 'change',
          },
        ],
        endTime: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value) return callback(new Error('請選擇時段結束時間'))
              if (minutesOfTime(this.submitData.startTime) >= minutesOfTime(value)) return callback(new Error('結束時間不可早於開始時間'))
              callback()
            },
            trigger: 'change',
          },
        ],
        repeatWith: [
          {
            type: 'array',
            required: repeatIsWeekMode,
            message: '請選擇重複的星期',
            trigger: 'change',
          },
        ],
        repeatEndDate: [
          {
            required: !repeatIsEmpty,
            message: '請選擇重複結束日期',
            trigger: 'change',
          },
        ],
        maxCollision: [
          {
            required: true,
            message: '請輸入人數',
          },
          { type: 'number', message: '人數必須為數字' },
        ],
        productIds: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (!value.length) {
                this.$nextTick(() => {
                  this.$refs.form.clearValidate('serviceIds')
                })
                return callback(new Error('請選擇服務地點'))
              }
              callback()
            },
            trigger: 'change',
          },
        ],
        serviceCategoryIds: [
          {
            required: true,
            message: `請選擇${this.$t('reserve.CLIENT')}類別與${this.$t('member.BODY_TO_TYPE')}`,
            trigger: 'change',
          }
        ],
        serviceIds: [
          {
            required: true,
            message: '請選擇服務項目',
            trigger: 'change',
          },
        ],
      }
    },
  },
  watch: {
    startAndOverTime(nvl, ovl) {
      let startHours = nvl[0].getHours()
      let startMinutes = nvl[0].getMinutes()
      let endHours = nvl[1].getHours()
      let endMinutes = nvl[1].getMinutes()
      if (startMinutes < 10) startMinutes = `0${startMinutes}`
      if (endMinutes < 10) endMinutes = `0${endMinutes}`
      if (startHours < 10) startHours = `0${startHours}`
      if (endHours < 10) endHours = `0${endHours}`
      this.$set(this.submitData, 'startTime', `${startHours}:${startMinutes}`)
      this.$set(this.submitData, 'endTime', `${endHours}:${endMinutes}`)
    },
    defaultValue: {
      handler: function (value) {
        // 新增mode（初始化在這邊處理）
        if (!this.isEditMode) {
          this.submitData = Object.assign({}, initData(), this.defaultValue)
          let stores = []
          this.locations.forEach(local => {
            this.submitData.productIds.forEach(item => {
              if (item === local.id) {
                stores.push(local)
              }
            })
          })
          this.setShowDayTime()
          this.$set(this.submitData, 'serviceCategoryIds', [])
          this.submitData.productIds = stores
        } else {
          // 修改mode
          const { startDate, endDate, productIds } = this.defaultValue
          this.submitData = {
            ...this.defaultValue,
            repeat: this.defaultValue.repeat || '',
            repeatWith: this.defaultValue.repeatWith || [],
            startTime: new Date(startDate.replace(/-/g, '/')).format('HH:mm'),
            endTime: new Date(endDate.replace(/-/g, '/')).format('HH:mm'),
            productIds: this.locations.filter((location) => productIds.includes(location.id)),
            storeIds: this.locations.filter((location) => productIds.includes(location.id)),
          }
          if (this.submitData.repeatWith.length > 0) {
            this.submitData.repeatWith = this.submitData.repeatWith.map((item) => String(item))
          }
          this.setShowDayTime()
          this.submitData.repeatWith.forEach(item => {
            this.updateScheduleItemDays(item)
          })

          delete this.submitData.startDate
          delete this.submitData.endDate
        }
      },
      immediate: true,
    },
  },
  async created() {
    await Promise.all([
      this.getCategoryList(),
      this.tryToFetchPrices()
    ])

  },
  mounted() {
    this.composeRestTimeFromDB()
    this.selectedDate = new Date(this.day)
  },
  methods: {
    /**
     * @description 編輯模式，要把原資料放回舊資料
     */
    composeRestTimeFromDB() {
      try {
        const { params } = this.defaultValue
        if (params && params.restTimes.length > 0) {
          params.restTimes.forEach(item => {
            let dbStartTime = item.start.split(':')
            let dbEndTime = item.end.split(':')
            this.timeRange.push({
              restTimes: [new Date(2016, 9, 10, dbStartTime[0], dbStartTime[1]), new Date(2016, 9, 10, dbEndTime[0], dbEndTime[1])]
            })
          })
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 新增時間區段
     */
    addLunchRest() {
      this.timeRange.push({ restTimes: ['', ''] })
    },
    /**
     * @description 刪除時間區段
     */
    deleteTime(index) {
      this.timeRange.splice(index, 1)
    },
    /**
     * @description 更新使用者選取的日子
     */
    updateScheduleItemDays(event) {
      try {
        const formattedDate = moment(event).format('YYYY-MM-DD');
        const dateIndex = this.calendarEvents.findIndex(item => item.start === formattedDate)
        if (dateIndex === -1) {
          this.calendarEvents.push({
            customInfo: {
              date: formattedDate,
              expired: true,
              isOpen: true,
              orderCount: 0
            },
            start: formattedDate,
            end: formattedDate,
          })
        } else {
          this.calendarEvents.splice(dateIndex, 1)
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    setShowDayTime() {
      let daySplit = this.day.split('-');
      let startTimeSplit = this.submitData.startTime.split(':');
      let endTimeSplit = this.submitData.endTime.split(':');
      this.startAndOverTime = [
        new Date(daySplit[0], daySplit[1], daySplit[2], startTimeSplit[0], startTimeSplit[1]),
        new Date(daySplit[0], daySplit[1], daySplit[2], endTimeSplit[0], endTimeSplit[1]),
      ]
    },
    cancelBtn() {
      this.$emit('returnBtn')
    },
    async getCategoryList() {
      let categoryAll = await this.$store.dispatch('product/fetchServiceCategory', { paginate: 0 })
      let newArray = []
      categoryAll.forEach((itemParent) => {
        itemParent.children.forEach((item) => {
          newArray.push(item)
        })
      })
      this.categoryList = newArray
    },
    /**
     * @description 獲取服務項目
     */
    async tryToFetchPrices() {
      const { items } = await this.$store.dispatch('product/fetchNormalServiceData', {
        types: this.defaultValue['staffOpenServiceTree']['searchTypes'] || ['wrongType'],
        paginate: 0
      })
      this.priceList = items
    },
    /**
     * @description 真正組資料 送api的地方
     */
    async tryToStoreItem() {
      try {
        this.filterSubmitData()
        this.tryingToStoreItem = true
        await this.$store.dispatch('schedule/storeItemDetail', {
          modifyMode: this.isModifyMode ? this.modifyMode : undefined,
          ...this.filterSubmitData(),
        })
        this.emitButtonEvent('store')
        if (!this.isEditMode) {
          messageStore('add')
          notification('排班', `${this.$t('prepaid.BASE')}方案`, 'prepaid')
        } else {
          messageStore('edit')
        }
        this.$emit('showOffOuterTab')
        this.dialogVisible = false
      } finally {
        this.tryingToStoreItem = false
      }
    },
    async tryToRemoveItem() {
      try {
        this.tryingToRemoveItem = true
        await this.$store.dispatch('schedule/removeItemDetail', {
          mode: this.modifyMode,
          id: this.defaultValue.id,
        })
        this.emitButtonEvent('store')
        this.emitButtonEvent('resetTab')
        this.dialogVisible = false
      } finally {
        this.tryingToRemoveItem = false
      }
    },
    /**
     * @description 組儲存資料
     */
    filterSubmitData() {
      try {
        const productIds = this.submitData.storeIds.map((item) => item.id)
        let result = {}
        if (this.submitData.repeat === 'calendar') {
          let repeatDayRaw = this.calendarEvents.map(item => item.end)
          let sortRepeatWithDays = repeatDayRaw.sort((a, b) => moment(a) - moment(b))
          let startEndDay = this.isEditMode ? [this.day] : sortRepeatWithDays
          result = Object.assign({}, this.submitData, {
            startDate: `${this.composeEndDate(startEndDay[0])[0]} ${this.submitData.startTime}`,
            endDate: `${this.composeEndDate(startEndDay[0])[1]} ${this.submitData.endTime}`,
            repeatEndDate: sortRepeatWithDays[sortRepeatWithDays.length - 1],
            productIds,
            repeatWith: sortRepeatWithDays,
            params: { 'restTimes': this.sortTimeRange(this.composeLunchRestTime()) }
          })
        }
        if (this.submitData.repeat !== 'calendar') {
          result = Object.assign({}, this.submitData, {
            startDate: `${this.composeEndDate(this.day)[0]} ${this.submitData.startTime}`,
            endDate: `${this.composeEndDate(this.day)[1]} ${this.submitData.endTime}`,
            repeatEndDate: formatRequestData(this.submitData.repeatEndDate),
            productIds,
            params: { 'restTimes': this.sortTimeRange(this.composeLunchRestTime()) }
          })
        }
        return result
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 組成結束日期（因為有可能跨日）
     */
    composeEndDate(day) {
      const startTime = moment(this.submitData.startTime, 'mm:ss')
      const endTime = moment(this.submitData.endTime, 'mm:ss')
      if (startTime.isBefore(endTime)) {
        // 正常沒跨日
        return [day, day]
      }
      if (startTime.isAfter(endTime)) {
        return [day, moment(day, 'YYYY-MM-DD').clone().add(1, 'day').format('YYYY-MM-DD')]
      }
    },
    /**
     * @description 組成午休時間格式
     */
    composeLunchRestTime() {
      try {
        return this.timeRange.reduce((accu, cur) => {
          if (cur.restTimes[0] && cur.restTimes[1]) {
            accu.push({
              start: moment(cur.restTimes[0]).format('HH:mm'),
              end: moment(cur.restTimes[1]).format('HH:mm')
            })
          }
          return accu
        }, [])
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 排序午休時間
     */
    sortTimeRange(timeRange) {
      try {
        return timeRange.sort((a, b) => {
          let timeA = a.start.split(':');
          let timeB = b.start.split(':');
          let minutesA = parseInt(timeA[0]) * 60 + parseInt(timeA[1]);
          let minutesB = parseInt(timeB[0]) * 60 + parseInt(timeB[1]);
          return minutesA - minutesB;
        })
      } catch (error) {
        console.error(error.message)
      }
    },
    emitButtonEvent(event) {
      this.$emit(event)
    },
    /**
     * @description 編輯排班的 儲存 or 建立
     */
    async onSave() {
      try {
        await this.$refs.form.validate()
        await this.validateResetTime()
        if (!this.$refs.serviceTree.validateSelf()) return
        if (this.isModifyMode) {
          this.removeMode = false
          this.dialogVisible = true
        } else {
          this.tryToStoreItem()
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 驗證休息時間是否可以通過
     */
    async validateResetTime() {
      try {
        await this.validateEmptyTime()
        await this.validateStartTime()
        await this.validateEndTime()
      } catch (error) {
        throw new Error(error.message)
      }
    },
    /**
     * @description 驗證時間是否為空
     */
    validateEmptyTime() {
      return new Promise((resolve, reject) => {
        this.timeRange.forEach(item => {
          const { restTimes } = item
          if (restTimes[0] === '' || restTimes[1] === '') {
            this.$message.error('請填入正確時間')
            reject(new Error('請填入正確時間t1'))
          }
        })
        resolve()
      })
    },
    /**
     * @description 驗證休息結束時間
     */
    validateEndTime() {
      return new Promise((resolve, reject) => {
        // 排班時間沒跨日，去驗證結束時間是否符合
        if (moment(this.submitData.startTime, 'HH:mm').isBefore(moment(this.submitData.endTime, 'HH:mm'))) {
          this.timeRange.forEach(item => {
            const { restTimes } = item
            let resetEndTime = moment(restTimes[1], 'HH:mm').format('HH:mm')
            let startTime = moment(this.submitData.startTime, 'HH:mm').format('HH:mm')
            let endTime = moment(this.submitData.endTime, 'HH:mm').format('HH:mm')
            if (resetEndTime < startTime || resetEndTime > endTime) {
              this.$message.error('請填入正確休息時間')
              reject(new Error('請填入正確休息時間t2'))
            }
          })
          resolve()
        }
        // 排班時間跨日，去驗證結束時間是否符合
        if (moment(this.submitData.startTime, 'HH:mm').isAfter(moment(this.submitData.endTime, 'HH:mm'))) {
          this.timeRange.forEach(item => {
            const { restTimes } = item
            let resetStartTime = moment(restTimes[0], 'HH:mm').format('HH:mm')
            let resetEndTime = moment(restTimes[1], 'HH:mm').format('HH:mm')
            let endTime = moment(this.submitData.endTime, 'HH:mm').format('HH:mm')
            if (resetEndTime > endTime && resetStartTime > resetEndTime) {
              this.$message.error('請填入正確休息時間')
              reject(new Error('請填入正確休息時間t3'))
            }
          })
          resolve()
        }
      })
    },
    /**
     * @description 驗證休息開始時間
     */
    validateStartTime() {
      return new Promise((resolve, reject) => {
        // 排班時間沒跨日驗證
        if (moment(this.submitData.startTime, 'HH:mm').isBefore(moment(this.submitData.endTime, 'HH:mm'))) {
          this.timeRange.forEach(item => {
            const { restTimes } = item
            let resetStartTime = moment(restTimes[0], 'HH:mm').format('HH:mm')
            let startTime = moment(this.submitData.startTime, 'HH:mm').format('HH:mm')
            let endTime = moment(this.submitData.endTime, 'HH:mm').format('HH:mm')
            if (resetStartTime < startTime || resetStartTime > endTime) {
              this.$message.error('請填入正確休息時間')
              reject(new Error('請填入正確休息時間t4'))
            }
          })
          resolve()
        }
        // 排班時間跨日的驗證，因為跨日，只測開始時間就好
        if (moment(this.submitData.startTime, 'HH:mm').isAfter(moment(this.submitData.endTime, 'HH:mm'))) {
          this.timeRange.forEach(item => {
            const { restTimes } = item
            let resetStartTime = moment(restTimes[0], 'HH:mm').format('HH:mm')
            let startTime = moment(this.submitData.startTime, 'HH:mm').format('HH:mm')
            if (resetStartTime < startTime) {
              this.$message.error('請填入正確休息時間')
              reject(new Error('請填入正確休息時間t5'))
            }
          })
          resolve()
        }
      })
    },
    async onRemove() {
      this.removeMode = true
      if (this.isModifyMode) this.dialogVisible = true
      else {
        await this.$confirm('確定刪除該時段？', '警告', {
          confirmButtonText: '確定',
          cancelButtonText: '取消',
          type: 'warning',
          center: true,
        })
        this.tryToRemoveItem()
      }
    },
    onLocationChange(selectedItem) {
      this.checkValidServices()
    },
    checkValidServices() {
      const productIds = this.submitData.productIds.map((product) => product.id)
      const validPrice = this.priceList.filter((price) => productIds.includes(price.productId)).map((price) => price.id)
      this.submitData.serviceIds = [...this.submitData.serviceIds.filter((serviceId) => validPrice.includes(serviceId))]
    },
    handleCheckAllChange(checked) {
      this.submitData.serviceIds = checked ? this.availableService.map((price) => price.id) : []
    },
    handleCheckAllCategory(checked) {
      this.submitData.serviceCategoryIds = checked ? this.categoryList.map((category) => category.id) : []
    },
  },
}
</script>
<template>
  <div>
    <h3 style="margin-top: 0">
      <span class="Jost" style="margin-right: 20px">{{ title }}</span>
      {{ staffName }}
    </h3>
    <el-form ref="form" :model="submitData" :rules="submitRules" label-position="top" class="el-form--floating"
      style="margin-top: 30px">
      <el-row :gutter="20">
        <!-- 開放預約 -->
        <el-col :span="24" :class="$style.booking">
          <span>是否開放預約</span>
          <el-switch v-model="submitData.state" active-text="是" inactive-text="否" :active-value="1" :inactive-value="0">
          </el-switch>
        </el-col>

        <!-- 是否允許超時 -->
        <el-col :span="24" :class="$style.booking">
          <span>是否允許超時</span>
          <el-switch v-model="submitData.canOverTime" active-text="是" inactive-text="否" :active-value="1"
            :inactive-value="0">
          </el-switch>
        </el-col>

        <el-col :span="24">
          <el-divider></el-divider>
        </el-col>
      </el-row>

      <el-row :class="$style.centerRepeatTime">
        <!-- 是否重複 -->
        <el-col :span="24">
          <el-form-item label="是否重複" prop="repeat">
            <el-select v-model="submitData.repeat">
              <el-option label="不重複" value=""></el-option>
              <el-option label="每天" value="day"></el-option>
              <el-option label="每週" value="week"></el-option>
              <el-option label="依月曆選取" value="calendar"></el-option>
            </el-select>
          </el-form-item>
        </el-col>
        <template v-if="submitData.repeat">
          <!-- 如果選擇為重複於每週的星期幾 -->
          <el-col v-if="submitData.repeat === 'week'" :span="24" :sm="12">
            <el-form-item label="重複於" prop="repeatWith">
              <el-checkbox-group v-model="submitData.repeatWith" :min="1" :class="$style.repeat">
                <el-checkbox-button v-for="(weekDay, index) in daysInWeek" :key="index" :label="String(index)">{{
                  weekDay
                }}</el-checkbox-button>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
          <!-- 選重複必定會出現 -->
          <el-col v-if="submitData.repeat !== 'calendar'" :span="submitData.repeat === 'week' ? 12 : 24">
            <el-form-item label="重複結束於" prop="repeatEndDate">
              <el-date-picker v-model="submitData.repeatEndDate" :default-value="new Date(day)"
                :picker-options="endDatePickerOptions" type="date" format="yyyy/MM/dd"></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col v-if="submitData.repeat === 'calendar'">
            <el-form-item label="重複於">
              <CalendarItem v-slot="{ data: { cell, events } }" props-width="100%" props-height="420px" :special="true"
                :selected-date.sync="selectedDate" :calendar-events="calendarEvents"
                @updateScheduleItemDays="updateScheduleItemDays">
                <span class="vuecal__cell-date"
                  :class="[events[0] && events[0].customInfo.isOpen ? 'is-available spWidth' : '',]">{{
                    cell.content }}</span>
              </CalendarItem>
            </el-form-item>
          </el-col>
        </template>

        <!-- 開始時間結束時間 -->
        <el-col :span="24">
          <el-form-item label="開始時間與結束時間" prop="repeat" :class="$style.centerRepeatTime__colRangeTime">
            <div>
              <el-time-picker v-model="startAndOverTime[0]" class="bg-white" format="HH:mm">
              </el-time-picker>
              <span>至</span>
              <el-time-picker v-model="startAndOverTime[1]" class="bg-white" format="HH:mm">
              </el-time-picker>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
      <!-- 可同時服務幾位顧客 -->
      <el-row :gutter="20" :class="$style.customerClass">
        <el-col :span="24">
          <el-divider></el-divider>
        </el-col>
        <el-col :span="24">
          <el-form-item label="可同時服務幾位顧客" prop="maxCollision">
            <el-input v-model.number="submitData.maxCollision" placeholder="請輸入數字" type="number"></el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :class="$style.serviceTreeUpper">
        <ServiceTree ref="serviceTree" v-model="submitData" class="serviceTreeUpper__padding" :locations.sync="locations"
          :default-value="defaultValue" />
      </el-row>
      <!-- 個人午休時間 -->
      <el-row :gutter="20" :class="$style.lunchBreak">
        <el-col :span="24" :class="$style.lunchBreak__input">
          <el-form-item label="個人休息時間">
            <template v-if="timeRange.length > 0">
              <div v-for="(timeItem, index) in timeRange" :key="index">
                <p>時段{{ index + 1 }}</p>
                <el-time-picker v-model="timeItem.restTimes[0]" class="bg-white is-border" :clearable="false"
                  format="HH:mm" placeholder="選擇開始休息時間">
                </el-time-picker>
                <span>至</span>
                <el-time-picker v-model="timeItem.restTimes[1]" class="bg-white is-border" :clearable="false"
                  format="HH:mm" placeholder="選擇結束休息時間">
                </el-time-picker>
                <el-button @click="deleteTime(index)">
                  <BaseIcon name="xmark" />
                </el-button>
              </div>
            </template>
          </el-form-item>
        </el-col>
        <el-col :span="24" :class="$style.lunchBreak__btn">
          <el-button class="is-round" @click="addLunchRest">
            + 新增個人休息時間
          </el-button>
        </el-col>
      </el-row>
    </el-form>
    <div :class="$style.footer">
      <el-button v-if="isEditMode" class="is-round" type="danger" plain :loading="tryingToRemoveItem"
        :disabled="tryingToRemoveItem" @click="onRemove">
        <BaseIcon name="trash-alt" />
      </el-button>
      <el-button class="is-round" @click="cancelBtn">取消</el-button>
      <el-button class="is-round" type="primary" :loading="tryingToStoreItem"
        :disabled="tryingToStoreItem || availableService.length === 0" @click="onSave">
        <BaseIcon name="check" />
        <span>{{ isEditMode ? '儲存' : '建立' }}</span>
      </el-button>
    </div>
    <BaseButtonDialog only-dialog :title="removeMode ? '刪除週期性排程' : '編輯週期性排程'" :dialog-visible.sync="dialogVisible"
      :width="'400px'" floating>
      <el-radio-group v-model="modifyMode" style="width: 100%; margin-bottom: 30px">
        <el-col :span="24" style="margin-bottom: 10px">
          <el-radio label="self" border style="width: 100%" class="is-round">只{{ removeMode ? '刪除' : '修改' }}此排程
          </el-radio>
        </el-col>
        <el-col :span="24">
          <el-radio label="henceforth" border style="width: 100%" class="is-round">{{ removeMode ? '刪除' : '修改'
          }}此排程和後續所有排程</el-radio>
        </el-col>
      </el-radio-group>
      <div :class="$style['footer-btn']">
        <el-button class="is-round" @click="dialogVisible = false">取消</el-button>
        <el-button class="is-round" :loading="removeMode ? tryingToRemoveItem : tryingToStoreItem"
          :disabled="removeMode ? tryingToRemoveItem : tryingToStoreItem" type="primary"
          @click="removeMode ? tryToRemoveItem() : tryToStoreItem()">確定</el-button>
      </div>
    </BaseButtonDialog>
  </div>
</template>
<style scoped>
.el-select>>>.el-select-dropdown {
  left: 0 !important;
  width: 100% !important;
  min-width: auto !important;
}
</style>
<style lang="scss" module>
@import '@src/design/layout_detail_steps.scss';

.repeat {
  :global {

    .el-checkbox-button {
      .el-checkbox-button__inner {
        border-radius: 8px;
        padding: 14px 15px;
        border-color: $color-primary;
      }

      &.is-checked {
        .el-checkbox-button__inner {
          background: $color-primary;
        }
      }

      &+.el-checkbox-button {
        margin-left: 5px;

        .el-checkbox-button__inner {
          border-left: 1px solid $color-primary;
        }
      }
    }
  }
}

.services-wrap {
  margin: 0 -5px 30px;
}

.services {
  :global {
    label.el-checkbox.is-bordered {
      width: calc(50% - 10px);
      display: inline-flex;
      margin: 5px;
      align-items: center;
      height: auto;
      padding: 15px 20px 15px 15px;

      @include max-sm {
        width: 100%;
      }

      .el-checkbox__inner {
        border-radius: 10px;
        width: 18px;
        height: 18px;

        &:after {
          left: 5px;
          top: 2px;
          height: 8px;
          width: 4px;
          border-width: 2px;
        }
      }

      .el-checkbox__label {
        padding-left: 15px;
        width: 100%;
      }

      &+.el-checkbox.is-bordered {
        margin-left: 5px;
      }

      .title,
      .meta {
        width: calc(100% - 20px);
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
      }

      .content {
        width: 100%;
      }
    }
  }
}

.footer-btn {
  display: flex;

  :global {
    button {
      flex: 1 0 auto;
    }
  }
}

.booking {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.centerRepeatTime {
  border: 1px solid $color-line-dark;
  padding: 14px 20px 2px;
  border-radius: $border-radius;

  :global {
    .el-select-dropdown {
      width: 200px;
    }

    .vuecal__cell-date {
      width: 44px !important;
      height: 44px !important;
      line-height: 44px;

      &.is-available {
        border-radius: 3px !important;
        background-color: $color-primary !important;
      }

    }
  }

  &__colRangeTime {
    display: flex;
    flex-direction: column;

    :global {
      .el-form-item__content {
        >div {
          display: flex;

          >span {
            margin-left: 10px;
            margin-right: 10px;
          }
        }

      }
    }
  }
}

.launchDisplay {
  margin: 24px auto;
  display: column;
  align-items: flex-start;

  div {
    display: flex;
    align-items: center;

    p {
      &:first-child {
        margin-right: 12px;

        &::after {
          content: '*';
          color: $color-primary;
        }
      }
    }
  }
}

.customerClass {
  :global {
    .el-select .el-input--suffix>.el-input__inner {
      height: 44px !important;
    }
  }
}

.serviceTreeUpper {
  margin-top: 10px;

  :global {
    .serviceTreeUpper__padding {
      padding-bottom: 0 !important;
    }
  }
}

.lunchBreak {
  margin-bottom: 30px;

  &__input {
    :global {
      .el-form-item__content {
        >div {
          background-color: $color-line;
          padding: 20px;
          margin-bottom: 14px;
          display: flex;
          justify-content: space-around;
          align-items: center;

          >p {
            width: 60px;
            min-width: 60px;
            margin-right: 18px;
          }

          >svg,
          span {
            margin-left: 10px;
            margin-right: 10px;
          }

          .el-input__inner {
            padding-left: 50px;
          }

          @media screen and (max-width:700px) {
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;

            >p,
            span,
            svg {
              margin-left: 10px;
              padding-left: 10px;
            }

            :global {
              .el-button {
                padding-left: 0;

                >span {
                  margin-left: 0;
                  padding-left: 0;
                }
              }
            }
          }
        }
      }

      .el-button {
        border: none;
        background-color: transparent;

        &:hover {
          background-color: transparent;
        }
      }
    }
  }

  &__btn {
    :global {
      .el-button {
        width: 100%;
      }
    }
  }
}
</style>
